import { useEffect, useMemo, useRef, useState } from "react";
import {
  Layers,
  ClipboardList,
  Wrench,
  MapPin,
  Car,
  Calculator,
  Loader2,
  Zap,
  User,
  Mail,
  Phone,
  ChevronRight,
  Info,
  AlertCircle,
  AlertTriangle,
  RotateCcw,
  Mailbox,
  Pencil,
  CalendarDays,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  WizardShell,
  OptionCard,
  PrimaryBtn,
  BackBtn,
  IdentityFields,
  FieldError,
  errorInputClass,
} from "@/components/mockups/WizardUI";
import SuccessModal from "@/components/mockups/SuccessModal";
import { publicApi, submitInbox, extractError, fetchServiceStatus, clearServiceStatusCache } from "@/lib/publicApi";
import { resolveImage } from "@/hooks/useCms";
import SuspendedNotice from "@/components/SuspendedNotice";
import Honeypot, { honeypotValue } from "@/components/Honeypot";
import {
  validateIdentity,
  focusFirstError,
  buildErrorSummary,
} from "@/lib/formValidation";
import { checkPostalDistrict } from "@/lib/postalDistrictMap";
import { usePostalAutofill } from "@/lib/usePostalAutofill";

const TOTAL_STEPS = 7;
const OTHER_STEP = 90; // ecrã especial para pedidos "Outro / não listado"
const OTHER_SOLUTION = {
  id: "__other__",
  slug: "outro",
  title: "Outro / Não listado",
  short_desc: "Quero saber mais sobre um setor que não está na lista",
  isOther: true,
};
const OTHER_SERVICE = {
  id: "__other__",
  slug: "outro",
  title: "Outro / Não listado",
  short_desc: "Quero saber mais sobre um serviço que não está na lista",
  isOther: true,
};
const LS_KEY = "inxpek_orcamento_progress_v1";
const fmt = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;

export default function OrcamentoDialog({ open, onClose, prefill = null }) {
  // ─── catálogo público ─────────────────────────────────────────────────────
  const [solutions, setSolutions] = useState([]);
  const [services, setServices] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  // Pares (solução × serviço) explicitamente desactivados no admin —
  // usados para esconder os serviços correspondentes no step 1.
  // Formato: Set com chaves "<solution_id>::<service_id>".
  const [disabledPairs, setDisabledPairs] = useState(() => new Set());

  // ─── seleção do utilizador ────────────────────────────────────────────────
  const [step, setStep] = useState(0);
  const [solution, setSolution] = useState(null);
  const [servico, setServico] = useState(null);
  const [districtId, setDistrictId] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [config, setConfig] = useState(null); // config pública (sanitizada) do par
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState("");
  const [answers, setAnswers] = useState({});
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState("");

  const [id, setId] = useState({});
  const [idTouched, setIdTouched] = useState({});
  const [morada, setMorada] = useState("");
  const [moradaUserEdited, setMoradaUserEdited] = useState(false);
  const [sending, setSending] = useState(false);
  // Quando o utilizador clica numa linha do resumo (step 5) para editar,
  // marcamos este flag para que o próximo "Continuar" o devolva directamente
  // ao resumo em vez de fazer avançar sequencialmente pelos passos seguintes.
  const [editingFromSummary, setEditingFromSummary] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [otherText, setOtherText] = useState("");
  const [prefilled, setPrefilled] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const otherMode = !!(solution?.isOther || servico?.isOther);

  // Auto-preenche a "Morada" a partir do Código Postal (base CTT).
  const { loading: postalLoading } = usePostalAutofill(
    postalCode,
    morada,
    setMorada,
    { userEdited: moradaUserEdited, enabled: open },
  );

  // ─── Aplicar prefill quando dialog abre com dados vindos do email
  // (links "Corrigir dados" / "Agendar agora") ou de outro contexto.
  // Corre assim que o catálogo tenha carregado. Só aplica uma vez por abertura.
  useEffect(() => {
    if (!open) {
      setPrefilled(false);
      return;
    }
    if (!prefill) return;
    if (prefilled) return;
    if (solutions.length === 0 || services.length === 0) return;
    const sol =
      solutions.find(
        (s) => s.id === prefill.solution_id || s.slug === prefill.solution_slug,
      ) || null;
    const svc =
      services.find(
        (s) => s.id === prefill.servico_id || s.slug === prefill.servico_slug,
      ) || null;
    if (sol) setSolution(sol);
    if (svc) setServico(svc);
    setId((prev) => ({
      ...prev,
      nome: prefill.nome || prev.nome || "",
      email: prefill.email || prev.email || "",
      telefone: prefill.telefone || prev.telefone || "",
    }));
    if (prefill.codigo_postal || prefill.postal_code) {
      setPostalCode(prefill.codigo_postal || prefill.postal_code);
    }
    // Distrito — restaura o campo ao "Corrigir Dados" (antes ficava em falta
    // mesmo com o código postal preservado, criando um estado inconsistente).
    if (prefill.district_id) {
      setDistrictId(prefill.district_id);
    }
    // Tipologia — restaura a escolha anterior para que o utilizador não tenha
    // de re-seleccionar tipologia ao corrigir só um pormenor (ex.: contacto).
    if (prefill.typology_id) {
      setAnswers((prev) => ({ ...prev, typology: prefill.typology_id }));
    }
    if (prefill.morada) {
      setMorada(prefill.morada);
      setMoradaUserEdited(true); // veio do prefill, considera-se "editada" para não sobrepor
    }
    // Salta para o passo apropriado — se houver solução + serviço → step 2 (distrito)
    if (sol && svc) setStep(2);
    else if (sol) setStep(1);
    setPrefilled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill, prefilled, solutions, services]);

  // ─── suspensão do serviço ─────────────────────────────────────────────────
  const [suspended, setSuspended] = useState(null); // { title, message } | null
  useEffect(() => {
    if (!open) {
      setSuspended(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const status = await fetchServiceStatus();
      if (cancelled) return;
      const svc = status?.orcamento;
      if (svc?.suspended) {
        setSuspended({ title: svc.title, message: svc.message });
      } else {
        setSuspended(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Safety net: se uma submissão falhar com 503 "service_suspended" (o serviço
  // foi desativado entretanto, ou o fetch inicial de estado falhou/estava em
  // cache), mostramos o MESMO aviso de "serviço em pausa" em vez de um erro
  // genérico — garante que a informação de "desativado" aparece sempre.
  const handleSuspendedError = (err) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 503 && detail && typeof detail === "object" && detail.code === "service_suspended") {
      setSuspended({
        title: detail.title || "Serviço em pausa",
        message: detail.message || "Este serviço está temporariamente indisponível.",
      });
      clearServiceStatusCache(); // força refetch atualizado na próxima abertura
      return true;
    }
    return false;
  };


  // ─── retoma de progresso (localStorage) ───────────────────────────────────
  const [resumeSnapshot, setResumeSnapshot] = useState(null);
  const pendingAnswersRef = useRef(null);
  // anti-spam (honeypot) — o form de identidade (step 5) desmonta antes do envio
  const hpRef = useRef(null);
  const hpValRef = useRef("");

  // ─── carregar catálogo ao abrir ───────────────────────────────────────────
  useEffect(() => {
    if (!open || solutions.length > 0) return;
    let cancelled = false;
    (async () => {
      setLoadingCatalog(true);
      setCatalogError("");
      try {
        const [solRes, svcRes, distRes, disabledRes] = await Promise.all([
          publicApi.get("/solutions"),
          publicApi.get("/services"),
          publicApi.get("/quote/districts"),
          publicApi.get("/quote/disabled-pairs").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setSolutions((solRes.data || []).filter((s) => s.published !== false));
        setServices((svcRes.data || []).filter((s) => s.published !== false));
        setDistricts(distRes.data || []);
        // Set de "<solution_id>::<service_id>" para lookup rápido no step 1
        const disabled = new Set();
        for (const p of disabledRes.data || []) {
          const sol = (p?.solution_id || "").trim();
          const svc = (p?.service_id || "").trim();
          if (sol && svc) disabled.add(`${sol}::${svc}`);
        }
        setDisabledPairs(disabled);
      } catch (e) {
        if (!cancelled) setCatalogError("Não foi possível carregar as opções.");
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, solutions.length]);

  // ─── carregar config do par (solução + serviço) quando ambos definidos ────
  useEffect(() => {
    if (solution?.isOther || servico?.isOther) {
      setConfig(null);
      return;
    }
    if (!solution || !servico) {
      setConfig(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingConfig(true);
      setConfigError("");
      setConfig(null);
      try {
        const { data } = await publicApi.get(
          `/quote/config/${solution.slug}/${servico.slug}`,
        );
        if (cancelled) return;
        setConfig(data);
        // Inicializar respostas com defaults (ou restaurar de progresso guardado)
        const init = {};
        (data.questions || []).forEach((q) => {
          if (q.type === "number") init[q.key] = q.default_value ?? q.min_value ?? 1;
          else if (q.type === "yesno") init[q.key] = false;
          else if (q.type === "single") init[q.key] = q.options?.[0]?.value || "";
          else if (q.type === "multi") init[q.key] = [];
        });
        if (pendingAnswersRef.current) {
          setAnswers({ ...init, ...pendingAnswersRef.current });
          pendingAnswersRef.current = null;
        } else {
          setAnswers(init);
        }
      } catch (e) {
        if (!cancelled) {
          const detail = e?.response?.data?.detail || "";
          const status = e?.response?.status;
          // 404 → serviço sem quote-config OU desactivado. Não é erro fatal:
          // o wizard deve encaminhar automaticamente para OTHER_STEP
          // ("Descreva o que pretende" + contactos). Guardamos um config
          // vazio para que hasTypologies=false e a lógica de routing normal
          // funcione (linha ~1033 + safety net linha ~291). Não pomos
          // configError para não bloquear o Continue no step 4.
          if (status === 404) {
            setConfig({
              solution: { id: solution?.id, slug: solution?.slug, title: solution?.title },
              service: { id: servico?.id, slug: servico?.slug, title: servico?.title },
              typologies: [],
              questions: [],
              postal_code_ranges: [],
              currency: "EUR",
              show_range: true,
              _no_config: true,
            });
            setAnswers({});
          } else {
            setConfigError(
              detail ||
                "Orçamento imediato indisponível para este serviço. Contacte-nos para uma proposta personalizada.",
            );
          }
        }
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [solution, servico]);

  // ─── safety net: se, por qualquer motivo, ficarmos no step 2 (Tipologia)
  // com um serviço real sem tipologias definidas, encaminha automaticamente
  // para o quadro "Descreva o que pretende" (OTHER_STEP)
  useEffect(() => {
    if (!open) return;
    if (step !== 2) return;
    if (!config) return;
    if (loadingConfig) return;
    if (solution?.isOther || servico?.isOther) return;
    const hasTyp = (config.typologies || []).length > 0;
    if (!hasTyp) setStep(OTHER_STEP);
  }, [open, step, config, loadingConfig, solution, servico]);

  // ─── serviços disponíveis para a solução escolhida ───────────────────────
  // Nota: pares (solução × serviço) explicitamente desactivados no admin
  // (`quote_configs.enabled=false`) são filtrados para respeitar a promessa
  // do toggle "Ativar orçamento imediato" — se OFF, o público não deve ver
  // o serviço aqui. Pares SEM config nenhuma continuam a aparecer (o wizard
  // encaminha-os para OTHER_STEP quando seleccionados).
  const servicesOfSolution = useMemo(() => {
    if (!solution) return [];
    const solId = solution.id || "";
    const isEnabled = (svc) => !disabledPairs.has(`${solId}::${svc.id}`);
    const ids = solution.service_ids || [];
    if (ids.length === 0) return services.filter(isEnabled);
    const byId = new Map(services.map((s) => [s.id, s]));
    return ids.map((sid) => byId.get(sid)).filter(Boolean).filter(isEnabled);
  }, [solution, services, disabledPairs]);

  // ─── validação identidade ────────────────────────────────────────────────
  const identityErrors = useMemo(
    () => validateIdentity(id, { phoneRequired: true, postalRequired: false }).errors,
    [id],
  );
  const setIdBlur = (field) => setIdTouched((t) => ({ ...t, [field]: true }));

  // ─── validação Distrito × Código Postal ──────────────────────────────────
  // Verificação leve (não bloqueante) baseada em intervalos de prefixos
  // conhecidos por distrito. Serve para alertar o utilizador quando escolhe
  // um distrito mas insere um CP claramente pertencente a outra região —
  // evita orçamentos enviesados por engano do utilizador.
  const districtCode = useMemo(
    () => districts.find((d) => d.id === districtId)?.code || "",
    [districts, districtId],
  );
  const districtNameByCode = useMemo(() => {
    const m = {};
    for (const d of districts) m[d.code] = d.name;
    return m;
  }, [districts]);
  const cpDistrictCheck = useMemo(
    () => checkPostalDistrict(districtCode, postalCode),
    [districtCode, postalCode],
  );

  // ─── verificação de cobertura por CP (chama /api/quote/coverage) ─────────
  // Devolve ao utilizador uma mensagem imediata: "zona coberta • deslocação
  // incluída / sem taxa" ou "fora de cobertura". Debounce 400ms.
  const [coverage, setCoverage] = useState(null);   // {covered, message, distance_applies, ...}
  const [coverageLoading, setCoverageLoading] = useState(false);
  useEffect(() => {
    // Só faz sentido quando temos par (solução/serviço) reais e CP com 4+ dígitos
    if (!open) return;
    if (!solution?.slug || !servico?.slug || solution.isOther || servico.isOther) {
      setCoverage(null);
      return;
    }
    const clean = String(postalCode || "").replace(/\D/g, "");
    if (clean.length < 4) {
      setCoverage(null);
      return;
    }
    let cancelled = false;
    setCoverageLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await publicApi.get("/quote/coverage", {
          params: {
            postal_code: postalCode,
            solution_slug: solution.slug,
            service_slug: servico.slug,
          },
        });
        if (!cancelled) setCoverage(data);
      } catch (_e) {
        if (!cancelled) setCoverage(null);
      } finally {
        if (!cancelled) setCoverageLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, postalCode, solution, servico]);


  // ─── validação respostas obrigatórias ─────────────────────────────────────
  const answersValid = useMemo(() => {
    if (!config) return true;
    // Tipologia obrigatória sempre que existirem tipologias definidas
    const typologies = config.typologies || [];
    let chosenTyp = null;
    if (typologies.length > 0) {
      const chosen = answers.typology;
      chosenTyp = typologies.find((t) => t.id === chosen) || null;
      if (!chosenTyp) return false;
    }
    const checkRequired = (q) => {
      if (!q.required) return true;
      const v = answers[q.key];
      if (q.type === "multi") return Array.isArray(v) && v.length > 0;
      if (q.type === "yesno") return true; // boolean é sempre válido
      if (q.type === "number") return !(v == null || v === "");
      return !(!v || String(v).trim() === "");
    };
    for (const q of config.questions || []) {
      if (!checkRequired(q)) return false;
    }
    // Perguntas complementares da tipologia escolhida
    for (const q of (chosenTyp?.questions || [])) {
      if (!checkRequired(q)) return false;
    }
    return true;
  }, [config, answers]);

  // ─── estimate: chamar /api/quote/estimate sempre que answers/distrito mudam
  // O cálculo arranca a partir do momento em que o utilizador entra no ecrã
  // de identidade (step 4) — assim, quando confirmar o pedido no step 5, a
  // estimativa já está pronta para ser apresentada no ecrã de sucesso, sem
  // ser exposta antecipadamente ao utilizador.
  useEffect(() => {
    if (step < 5) return;
    if (!config || !districtId || !answersValid) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    setEstimateLoading(true);
    setEstimateError("");
    (async () => {
      try {
        const { data } = await publicApi.post("/quote/estimate", {
          solution_slug: solution.slug,
          service_slug: servico.slug,
          district_id: districtId,
          postal_code: postalCode || undefined,
          answers,
        });
        if (cancelled) return;
        setEstimate(data);
      } catch (e) {
        if (cancelled) return;
        setEstimateError(extractError(e, "Falha a calcular estimativa."));
      } finally {
        if (!cancelled) setEstimateLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, config, districtId, postalCode, answers, answersValid, solution, servico]);

  // ─── reset ────────────────────────────────────────────────────────────────
  const resetState = () => {
    setStep(0);
    setSolution(null);
    setServico(null);
    setDistrictId("");
    setPostalCode("");
    setConfig(null);
    setAnswers({});
    setEstimate(null);
    setId({});
    setIdTouched({});
    setMorada("");
    setMoradaUserEdited(false);
    setSending(false);
    setEditingFromSummary(false);
    setOtherText("");
  };
  const close = () => {
    // Se o utilizador já avançou além do step 0, confirmar antes de fechar
    // usando um modal aninhado (evita o browser confirm nativo).
    if (step > 0) {
      setShowExitConfirm(true);
      return;
    }
    resetState();
    onClose();
  };
  const confirmExit = () => {
    setShowExitConfirm(false);
    resetState();
    onClose();
  };
  const cancelExit = () => setShowExitConfirm(false);

  // ─── persistência de progresso (localStorage) ────────────────────────────
  const clearProgress = () => {
    try {
      localStorage.removeItem(LS_KEY);
    } catch (e) {
      /* noop */
    }
    setResumeSnapshot(null);
  };

  // Guardar snapshot sempre que há progresso significativo enquanto o wizard está aberto
  useEffect(() => {
    if (!open) return;
    if (step === 0 && !solution) return; // nada de relevante ainda
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({
          step,
          solutionId: solution?.id || null,
          servicoId: servico?.id || null,
          districtId,
          postalCode,
          answers,
          id,
          morada,
          otherText,
          ts: Date.now(),
        }),
      );
    } catch (e) {
      /* noop */
    }
  }, [open, step, solution, servico, districtId, postalCode, answers, id, morada, otherText]);

  // Ao abrir, verificar se existe progresso por terminar para oferecer retoma
  useEffect(() => {
    if (!open) {
      setResumeSnapshot(null);
      return;
    }
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const snap = JSON.parse(raw);
        if (snap && snap.step > 0 && snap.solutionId) setResumeSnapshot(snap);
        else setResumeSnapshot(null);
      } else {
        setResumeSnapshot(null);
      }
    } catch (e) {
      setResumeSnapshot(null);
    }
  }, [open]);

  const resumeProgress = () => {
    const snap = resumeSnapshot;
    if (!snap) return;
    const sol =
      snap.solutionId === OTHER_SOLUTION.id
        ? OTHER_SOLUTION
        : solutions.find((s) => s.id === snap.solutionId) || null;
    const svc =
      snap.servicoId === OTHER_SERVICE.id
        ? OTHER_SERVICE
        : services.find((s) => s.id === snap.servicoId) || null;
    if (snap.answers) pendingAnswersRef.current = snap.answers;
    if (sol) setSolution(sol);
    if (svc) setServico(svc);
    setDistrictId(snap.districtId || "");
    setPostalCode(snap.postalCode || "");
    if (snap.id) setId(snap.id);
    if (snap.morada) {
      setMorada(snap.morada);
      setMoradaUserEdited(true);
    }
    if (snap.otherText) setOtherText(snap.otherText);
    setResumeSnapshot(null);
    setStep(snap.step || 0);
  };

  const discardProgress = () => {
    clearProgress();
    resetState();
  };

  // ─── submissão final ─────────────────────────────────────────────────────
  const finish = async () => {
    if (sending) return;
    const { isValid, errors, firstErrorField } = validateIdentity(id, {
      phoneRequired: true,
      postalRequired: false,
    });
    if (!isValid) {
      setIdTouched({ nome: true, email: true, telefone: true });
      toast.error(buildErrorSummary(errors));
      focusFirstError(firstErrorField, "orc");
      return;
    }
    setSending(true);
    try {
      // Reconstruir respostas com labels para email/inbox
      const answersLabelled = {};
      // Tipologia primeiro (se aplicável) — aparece no topo do resumo
      const chosenTypology = (config?.typologies || []).find((t) => t.id === answers.typology);
      const typologyLabel = chosenTypology ? (config.typology_label || "Tipologia") : "";
      const typologyValue = chosenTypology ? chosenTypology.label : "";
      if (chosenTypology) {
        answersLabelled[config.typology_label || "Tipologia"] = chosenTypology.label;
      }
      for (const q of config?.questions || []) {
        const v = answers[q.key];
        const opts = (q.options || []).reduce((a, o) => ({ ...a, [o.value]: o.label }), {});
        if (q.type === "multi") answersLabelled[q.label] = (v || []).map((x) => opts[x] || x);
        else if (q.type === "yesno") answersLabelled[q.label] = v ? "Sim" : "Não";
        else if (q.type === "number") answersLabelled[q.label] = v;
        else answersLabelled[q.label] = opts[v] || v;
      }
      // Perguntas complementares da tipologia escolhida (todas as variáveis selecionadas)
      for (const q of (chosenTypology?.questions || [])) {
        const v = answers[q.key];
        if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
        const opts = (q.options || []).reduce((a, o) => ({ ...a, [o.value]: o.label }), {});
        if (q.type === "multi") answersLabelled[q.label] = (v || []).map((x) => opts[x] || x);
        else if (q.type === "yesno") answersLabelled[q.label] = v ? "Sim" : "Não";
        else if (q.type === "number") answersLabelled[q.label] = v;
        else answersLabelled[q.label] = opts[v] || v;
      }
      const district = districts.find((d) => d.id === districtId);
      const rangeStr = estimate?.show_range
        ? `${fmt(estimate.min_price)} – ${fmt(estimate.max_price)}`
        : fmt(estimate?.mid_price || 0);
      // Detecta se o valor final é 0€ (sistema não conseguiu estimar) — nesse
      // caso mostramos uma mensagem alternativa a informar que a equipa entra
      // em contacto em vez do orçamento reveal.
      const _midVal = Number(estimate?.mid_price || 0);
      const _minVal = Number(estimate?.min_price || 0);
      const _maxVal = Number(estimate?.max_price || 0);
      const isZeroEstimate = _midVal <= 0 && _minVal <= 0 && _maxVal <= 0;
      // Custo de transporte (deslocação) — mostramos SEMPRE (Grátis ou valor real)
      // porque o utilizador quer saber, no resumo, se está a pagar deslocação.
      const transportSurcharge = Number(estimate?.distance_surcharge || 0);
      const transportTier = (estimate?.distance_tier_label || "").trim();
      const transportKm = Number(estimate?.distance_km || 0);
      const hasTransport = transportSurcharge > 0;
      // Linha detalhada (com tier + km) — usada no message/email admin
      const transportLine = hasTransport
        ? `${fmt(transportSurcharge)}${transportTier ? ` · ${transportTier}` : ""}${transportKm ? ` (~${Math.round(transportKm)} km)` : ""}`
        : "Grátis (incluído)";
      // Linha só com o valor — usada no SuccessModal (o cliente quer ver só o preço)
      const transportValueOnly = hasTransport ? fmt(transportSurcharge) : "Grátis (incluído)";
      const messageLines = [
        `Pedido de orçamento imediato:`,
        `• Solução: ${solution.title}`,
        `• Serviço: ${servico.title}`,
      ];
      // Tipologia logo a seguir ao serviço (destaque)
      if (typologyLabel && typologyValue) {
        messageLines.push(`• ${typologyLabel}: ${typologyValue}`);
      }
      messageLines.push(`• Distrito: ${district?.name || ""}`);
      if (postalCode) messageLines.push(`• Código postal: ${postalCode}`);
      if (morada.trim()) messageLines.push(`• Morada do local: ${morada.trim()}`);
      // Restantes respostas EXCEPTO a tipologia (para não duplicar) e excepto perguntas
      // cuja label seja idêntica ao label da tipologia. Colocadas antes das
      // linhas de preço para que o valor apareça como última informação.
      Object.entries(answersLabelled).forEach(([k, v]) => {
        if (typologyLabel && k === typologyLabel) return; // já inserido acima
        messageLines.push(`• ${k}: ${Array.isArray(v) ? v.join(", ") : v}`);
      });
      // Valor do serviço = total SEM a deslocação (o subtotal do servidor já
      // inclui a sobretaxa de deslocação, por isso subtraímo-la aqui).
      const serviceRangeStr = estimate?.show_range
        ? `${fmt(Math.max(0, (Number(estimate.min_price) || 0) - transportSurcharge))} – ${fmt(Math.max(0, (Number(estimate.max_price) || 0) - transportSurcharge))}`
        : fmt(Math.max(0, (Number(estimate?.mid_price) || 0) - transportSurcharge));
      // Valor do serviço — apresentado antes da deslocação
      messageLines.push(`• Valor do serviço: ${serviceRangeStr}`);
      // Valor da deslocação — sempre presente (Grátis ou valor)
      messageLines.push(`• Valor da deslocação: ${transportLine}`);
      // Orçamento — última informação do resumo (foco do pedido, valor real)
      messageLines.push(`• Orçamento: ${rangeStr} (IVA à taxa legal em vigor incluído)`);
      // Textos livres — última secção antes do payload (surgem também no email admin)
      const _typNote = (chosenTypology?.note || "").trim();
      const _svcNote = (config?.intro_message || "").trim();
      if (_typNote) messageLines.push(`• Observações: ${_typNote}`);
      if (_svcNote) messageLines.push(`• Nota: ${_svcNote}`);
      // Payload completo para reabrir o wizard mais tarde (snapshot Mongo).
      // Inclui tudo o que os wizards Orçamento/Agendamento conseguem consumir
      // via prop `prefill`. Usado pelos botões dos emails "Corrigir dados" /
      // "Agendar agora" através de `?open=<x>&pid=<uuid>`.
      const prefillPayloadFull = {
        solution_id: solution.id,
        solution_slug: solution.slug,
        servico_id: servico.id,
        servico_slug: servico.slug,
        codigo_postal: postalCode || "",
        morada: morada.trim() || "",
        district_id: districtId || "",
        district_name: district?.name || "",
        nome: id.nome,
        email: id.email,
        telefone: id.telefone || "",
        typology_id: chosenTypology?.id || "",
        typology_label: typologyLabel || "",
        typology_value: typologyValue || "",
        // Textos livres (herdados no Agendamento via "Agendar Online agora")
        service_note: config?.intro_message || "",
        typology_note: chosenTypology?.note || "",
        answers,
        answers_labelled: answersLabelled,
        transport_surcharge: hasTransport ? transportSurcharge : 0,
        transport_km: hasTransport ? transportKm : null,
        transport_tier: hasTransport ? transportTier : "",
        origin_postal_code: estimate?.origin_postal_code || "",
        origin_place: estimate?.distance_from_place || "",
        destination_place: estimate?.distance_to_place || "",
      };
      await submitInbox({
        type: "orcamento",
        name: id.nome,
        email: id.email,
        phone: id.telefone || "",
        subject: `Orçamento · ${solution.title} · ${servico.title} · ${district?.name || ""}`,
        message: messageLines.join("\n"),
        extra: {
          website: honeypotValue(hpRef, hpValRef),
          solution_id: solution.id,
          solution_slug: solution.slug,
          solution_title: solution.title,
          servico_id: servico.id,
          servico_slug: servico.slug,
          servico_label: servico.title,
          district_id: districtId,
          district_name: district?.name || "",
          postal_code: postalCode || "",
          morada: morada.trim() || null,
          answers,
          answers_labelled: answersLabelled,
          typology_label: typologyLabel || null,
          typology_value: typologyValue || null,
          typology_id: chosenTypology?.id || null,
          // Textos livres: "Nota" (do serviço) + "Observações" (da tipologia).
          // Só ficam guardados se não vazios. Renderizados no SuccessModal e
          // nos emails auto/notify quando presentes.
          service_note: (config?.intro_message || "").trim() || null,
          typology_note: (chosenTypology?.note || "").trim() || null,
          transport_surcharge: hasTransport ? transportSurcharge : 0,
          transport_km: hasTransport ? transportKm : null,
          transport_tier: hasTransport ? transportTier : "",
          // Ponto de partida da deslocação (só visível no admin, não no email do cliente)
          origin_postal_code: estimate?.origin_postal_code || "",
          origin_place: estimate?.distance_from_place || "",
          destination_place: estimate?.distance_to_place || "",
          estimate_min: estimate?.min_price,
          estimate_max: estimate?.max_price,
          estimate_mid: estimate?.mid_price,
          currency: estimate?.currency || "EUR",
          // Snapshot completo (backend guarda em quote_prefills e devolve pid
          // dentro de extra.prefill_id — usado pelos botões dos emails)
          prefill_payload: prefillPayloadFull,
        },
      });
      // Dados de prefill guardados para o atalho "Agendar Online" (sessão actual)
      const prefillPayload = prefillPayloadFull;
      setSuccessData({
        name: id.nome,
        email: id.email,
        highlight: isZeroEstimate
          ? {
              label: "Pedido recebido",
              value: "Análise pela nossa equipa",
              note: "Não foi possível determinar o valor para o serviço pretendido. A nossa equipa entrará em contacto no prazo de 24h úteis.",
            }
          : {
              label: "O seu orçamento",
              value: rangeStr,
              note: "IVA à taxa legal em vigor incluído.",
            },
        details: [
          { icon: Layers, label: "Solução", value: solution.title },
          { icon: Wrench, label: "Serviço", value: servico.title },
          ...(typologyLabel && typologyValue
            ? [{ icon: ClipboardList, label: typologyLabel, value: typologyValue }]
            : []),
          // Respostas às perguntas complementares (excluindo a tipologia já mostrada acima).
          // Uma linha por resposta — o utilizador vê o que respondeu no wizard.
          ...Object.entries(answersLabelled)
            .filter(([k]) => !typologyLabel || k !== typologyLabel)
            .map(([k, v]) => ({
              icon: ClipboardList,
              label: k,
              value: Array.isArray(v) ? v.join(", ") : String(v),
            })),
          { icon: MapPin, label: "Distrito", value: `${district?.name || ""}${postalCode ? ` · ${postalCode}` : ""}` },
          // Morada — só se o utilizador a preencheu (auto-preenchida por CP ou manual)
          ...(morada.trim()
            ? [{ icon: MapPin, label: "Morada", value: morada.trim(), multiline: true }]
            : []),
          // Valor do serviço (sem deslocação) — só quando há estimativa válida.
          ...(!isZeroEstimate
            ? [{ icon: Calculator, label: "Valor do serviço", value: serviceRangeStr }]
            : []),
          // Custo de deslocação — mostra apenas o valor (o cliente pediu para não
          // mostrar aqui a distância nem o intervalo de km — essa info fica no
          // message/email do admin através de `transportLine`).
          { icon: Car, label: "Custo de deslocação", value: transportValueOnly },
          // Observações (por tipologia) — só se a tipologia escolhida tiver texto.
          ...((chosenTypology?.note || "").trim()
            ? [{ icon: ClipboardList, label: "Observações", value: chosenTypology.note.trim(), multiline: true }]
            : []),
          // Nota (por serviço) — texto livre configurado no admin do serviço.
          ...((config?.intro_message || "").trim()
            ? [{ icon: Info, label: "Nota", value: config.intro_message.trim(), multiline: true }]
            : []),
        ],
        // Frase de reforço de confiança sob o resumo — muda conforme o valor
        // apurado. Só falamos das Condições Particulares quando temos preço válido.
        assurance: isZeroEstimate
          ? "A nossa equipa técnica responde tipicamente em até 24h úteis."
          : "Com as Condições Particulares de Prestação do Serviço.",
        // Rótulo do botão de fechar — passa a permitir corrigir os dados do pedido
        closeLabel: "Corrigir Dados",
        // Frase que aparece antes do botão "Agendar Online agora"
        secondaryTagline: isZeroEstimate
          ? ""
          : "Agende já o serviço, para a data da sua preferência.",
        isZeroEstimate,
        prefill: prefillPayload,
      });
      // limpar progresso guardado após submissão bem-sucedida
      clearProgress();
      onClose();
      resetState();
    } catch (err) {
      setSending(false);
      if (handleSuspendedError(err)) return;
      toast.error(extractError(err, "Não foi possível registar o pedido. Tente novamente."));
    }
  };

  // ─── submissão do fluxo "Outro / não listado" ────────────────────────────
  const finishOther = async () => {
    if (sending) return;
    if (!otherText || otherText.trim().length < 5) {
      toast.error("Descreva o que pretende (mínimo 5 caracteres).");
      return;
    }
    const { isValid, errors, firstErrorField } = validateIdentity(id, {
      phoneRequired: true,
      postalRequired: false,
    });
    if (!isValid) {
      setIdTouched({ nome: true, email: true, telefone: true });
      toast.error(buildErrorSummary(errors));
      focusFirstError(firstErrorField, "orc");
      return;
    }
    setSending(true);
    try {
      const isOtherFlow = !!(solution?.isOther || servico?.isOther);
      const solLabel = solution?.isOther ? "Outro / Não listado" : solution?.title || "";
      const svcLabel = servico?.isOther ? "Outro / Não listado" : servico?.title || "";
      const messageHeader = isOtherFlow
        ? "Pedido de orçamento — solução/serviço não listado:"
        : "Pedido de orçamento — descrição personalizada:";
      const messageLines = [
        messageHeader,
        `• Solução: ${solLabel}`,
        `• Serviço: ${svcLabel}`,
        `• Descrição do cliente: ${otherText.trim()}`,
      ];
      await submitInbox({
        type: "orcamento",
        name: id.nome,
        email: id.email,
        phone: id.telefone || "",
        subject: isOtherFlow
          ? "Orçamento · Outro (não listado)"
          : "Orçamento · Descrição personalizada",
        message: messageLines.join("\n"),
        extra: {
          website: honeypotValue(hpRef, hpValRef),
          is_other: isOtherFlow,
          solution_id: solution?.isOther ? null : solution?.id,
          solution_slug: solution?.isOther ? null : solution?.slug,
          solution_title: solLabel,
          servico_id: servico?.isOther ? null : servico?.id,
          servico_slug: servico?.isOther ? null : servico?.slug,
          servico_label: svcLabel,
          other_description: otherText.trim(),
          // Snapshot compacto para os botões dos emails ("Corrigir dados")
          prefill_payload: {
            solution_id: solution?.isOther ? "" : (solution?.id || ""),
            solution_slug: solution?.isOther ? "" : (solution?.slug || ""),
            servico_id: servico?.isOther ? "" : (servico?.id || ""),
            servico_slug: servico?.isOther ? "" : (servico?.slug || ""),
            nome: id.nome,
            email: id.email,
            telefone: id.telefone || "",
            other_description: otherText.trim(),
          },
        },
      });
      setSuccessData({
        name: id.nome,
        email: id.email,
        details: [
          { icon: Layers, label: "Solução", value: solLabel },
          { icon: Wrench, label: "Serviço", value: svcLabel },
          { icon: Info, label: "O seu pedido", value: otherText.trim() },
        ],
      });
      clearProgress();
      onClose();
      resetState();
    } catch (err) {
      setSending(false);
      if (handleSuspendedError(err)) return;
      toast.error(extractError(err, "Não foi possível registar o pedido. Tente novamente."));
    }
  };

  // ─── footer por passo ────────────────────────────────────────────────────
  const hasTypologies = (config?.typologies || []).length > 0;
  // Tipologia escolhida e as suas perguntas complementares (dinâmico)
  const chosenTypologyObj = (config?.typologies || []).find((t) => t.id === answers.typology) || null;
  const typologyQuestions = chosenTypologyObj?.questions || [];
  const hasQuestions = ((config?.questions || []).length > 0) || (typologyQuestions.length > 0);

  // ─── descrição por passo ─────────────────────────────────────────────────
  const stepDesc = (() => {
    switch (step) {
      case 0:
        return "Escolha o setor que se aplica ao seu caso.";
      case 1:
        return "Selecione o serviço que pretende.";
      case 2:
        return "Selecione uma tipologia.";
      case 3: {
        return "Responda para afinarmos o orçamento.";
      }
      case 4:
        return "Só mais um passo.";
      case 5:
        return "Insira os seus dados para receber o orçamento.";
      case 6:
        return "Confirme os seus dados. Ao confirmar, revelamos o orçamento.";
      case OTHER_STEP:
        return "Descreva o que pretende e deixe os seus dados. A nossa equipa entra em contacto dentro de 24h úteis.";
      default:
        return "";
    }
  })();
  // Validações separadas: tipologia (step 3) vs perguntas (step 4)
  const typologyValid = !hasTypologies || Boolean(
    answers.typology && (config?.typologies || []).some((t) => t.id === answers.typology),
  );
  const questionsValid = useMemo(() => {
    if (!config) return true;
    const chosenTyp = (config.typologies || []).find((t) => t.id === answers.typology) || null;
    const checkRequired = (q) => {
      if (!q.required) return true;
      const v = answers[q.key];
      if (q.type === "multi") return Array.isArray(v) && v.length > 0;
      if (q.type === "yesno") return true;
      if (q.type === "number") return !(v == null || v === "");
      return !(!v || String(v).trim() === "");
    };
    for (const q of config.questions || []) {
      if (!checkRequired(q)) return false;
    }
    for (const q of (chosenTyp?.questions || [])) {
      if (!checkRequired(q)) return false;
    }
    return true;
  }, [config, answers]);
  // Passos activos (0..6). Nova ordem: 0=Solução, 1=Serviço, 2=Tipologia,
  // 3=Perguntas, 4=Localização, 5=Identidade, 6=Resumo.
  // Salta 2 se não houver tipologias; salta 3 se não houver perguntas.
  const activeSteps = useMemo(() => {
    const base = [0, 1];
    if (hasTypologies) base.push(2);
    if (hasQuestions) base.push(3);
    base.push(4, 5, 6);
    return base;
  }, [hasTypologies, hasQuestions]);
  // Próximo/anterior passo tendo em conta o salto de 3 e/ou 4
  const nextAfter = (current) => {
    const idx = activeSteps.indexOf(current);
    if (idx < 0 || idx >= activeSteps.length - 1) return current;
    return activeSteps[idx + 1];
  };
  const prevBefore = (current) => {
    const idx = activeSteps.indexOf(current);
    if (idx <= 0) return current;
    return activeSteps[idx - 1];
  };

  const footer = (() => {
    // Helper: quando estamos a editar a partir do resumo, o botão "Continuar"
    // devolve-nos ao step 6 (resumo). Excepção: se ainda houver tipologia/
    // perguntas obrigatórias por responder, redireccionamos primeiro para o
    // step correcto (2 = tipologia, 3 = perguntas).
    const finishEdit = () => {
      if (config && hasTypologies && !typologyValid) {
        setStep(2);
        return;
      }
      if (config && hasQuestions && !questionsValid) {
        setStep(3);
        return;
      }
      setEditingFromSummary(false);
      setStep(6);
    };
    const nextLabel = editingFromSummary ? "Guardar e voltar" : "Continuar";

    if (step === 0) {
      return (
        <>
          {editingFromSummary && <BackBtn testId="orc-back-edit" onClick={finishEdit} />}
          <PrimaryBtn
            testId="orc-next-0"
            disabled={!solution || loadingCatalog}
            onClick={() => {
              // Em modo edição: se ao trocar de solução ficámos sem serviço
              // válido, forçamos o utilizador a escolher um novo antes de
              // regressar ao resumo. Caso contrário salta directo ao resumo.
              if (editingFromSummary) {
                if (solution?.isOther) return setStep(OTHER_STEP);
                if (!servico) return setStep(1);
                return finishEdit();
              }
              setStep(solution?.isOther ? OTHER_STEP : 1);
            }}
          >
            {editingFromSummary && !servico ? "Continuar" : nextLabel}
          </PrimaryBtn>
        </>
      );
    }
    if (step === 1) {
      return (
        <>
          <BackBtn
            testId="orc-back-1"
            onClick={editingFromSummary ? finishEdit : () => setStep(0)}
          />
          <PrimaryBtn
            testId="orc-next-1"
            disabled={!servico || loadingConfig}
            onClick={() => {
              if (editingFromSummary && !servico?.isOther) return finishEdit();
              if (servico?.isOther) return setStep(OTHER_STEP);
              // Se o serviço real não tem tipologia definida, encaminhar para
              // o quadro "Descreva o que pretende" (mesmo fluxo do Outro/Não listado)
              if (config && !hasTypologies) return setStep(OTHER_STEP);
              setStep(2);
            }}
          >
            {nextLabel}
          </PrimaryBtn>
        </>
      );
    }
    if (step === 2) {
      // TIPOLOGIA (obrigatória sempre que existir)
      return (
        <>
          <BackBtn
            testId="orc-back-2"
            onClick={editingFromSummary ? finishEdit : () => setStep(prevBefore(2))}
          />
          <PrimaryBtn
            testId="orc-next-2"
            onClick={() => {
              if (!typologyValid) {
                toast.error(`Escolha a ${(config?.typology_label || "tipologia").toLowerCase()}.`);
                return;
              }
              if (editingFromSummary) return finishEdit();
              setStep(nextAfter(2));
            }}
          >
            {nextLabel}
          </PrimaryBtn>
        </>
      );
    }
    if (step === 3) {
      // PERGUNTAS (todas as outras)
      return (
        <>
          <BackBtn
            testId="orc-back-3"
            onClick={editingFromSummary ? finishEdit : () => setStep(prevBefore(3))}
          />
          <PrimaryBtn
            testId="orc-next-3"
            onClick={() => {
              if (!questionsValid) {
                toast.error("Responda às perguntas obrigatórias.");
                return;
              }
              if (editingFromSummary) return finishEdit();
              setStep(nextAfter(3));
            }}
          >
            {nextLabel}
          </PrimaryBtn>
        </>
      );
    }
    if (step === 4) {
      // LOCALIZAÇÃO (distrito + CP) — no final, antes da identidade
      // Validamos o formato do código postal (NNNN-NNN) antes de avançar.
      const cpRegex = /^\d{4}-\d{3}$/;
      const cpValid = cpRegex.test((postalCode || "").trim());
      return (
        <>
          <BackBtn
            testId="orc-back-4"
            onClick={editingFromSummary ? finishEdit : () => setStep(prevBefore(4))}
          />
          <PrimaryBtn
            testId="orc-next-4"
            disabled={!districtId || !cpValid}
            onClick={() => {
              if (!districtId) {
                toast.error("Selecione o distrito.");
                return;
              }
              if (!cpValid) {
                toast.error("Introduza um código postal válido (NNNN-NNN).");
                document.getElementById("orc-postal")?.focus();
                return;
              }
              if (configError) {
                toast.error(configError);
                return;
              }
              if (editingFromSummary) return finishEdit();
              setStep(nextAfter(4));
            }}
          >
            {nextLabel}
          </PrimaryBtn>
        </>
      );
    }
    if (step === 5) {
      // Identidade + Morada + Resumo — dispara o envio; estimativa é
      // revelada no ecrã de sucesso. Passo final do wizard.
      return (
        <>
          <BackBtn
            testId="orc-back-5"
            onClick={
              editingFromSummary ? finishEdit : () => setStep(prevBefore(5))
            }
          />
          <PrimaryBtn
            testId="orc-next-5"
            disabled={sending || estimateLoading || !!estimateError || !estimate}
            onClick={() => {
              const { isValid, errors, firstErrorField } = validateIdentity(id, {
                phoneRequired: true,
                postalRequired: false,
              });
              if (!isValid) {
                setIdTouched({ nome: true, email: true, telefone: true });
                toast.error(buildErrorSummary(errors));
                focusFirstError(firstErrorField, "orc");
                return;
              }
              if (editingFromSummary) return finishEdit();
              finish();
            }}
          >
            {editingFromSummary ? "Guardar e voltar" : (
              sending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> A enviar…
                </span>
              ) : (
                "Avançar com pedido"
              )
            )}
          </PrimaryBtn>
        </>
      );
    }
    if (step === 6) {
      // Confirmação final — dispara o envio; estimativa é revelada no ecrã de sucesso
      return (
        <>
          <BackBtn testId="orc-back-6" onClick={() => setStep(5)} />
          <PrimaryBtn
            testId="orc-submit"
            disabled={sending || estimateLoading || !!estimateError || !estimate}
            onClick={finish}
          >
            {sending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> A enviar…
              </span>
            ) : (
              "Confirmar Pedido"
            )}
          </PrimaryBtn>
        </>
      );
    }
    if (step === OTHER_STEP) {
      return (
        <>
          <BackBtn
            testId="orc-back-other"
            onClick={() => setStep(solution?.isOther ? 0 : 1)}
          />
          <PrimaryBtn testId="orc-submit-other" disabled={sending} onClick={finishOther}>
            {sending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> A enviar…
              </span>
            ) : (
              "Enviar Pedido"
            )}
          </PrimaryBtn>
        </>
      );
    }
    return null;
  })();

  return (
    <>
      <WizardShell
        open={open}
        onClose={close}
        testId="dialog-orcamento"
        step={suspended ? 1 : step === OTHER_STEP ? (solution?.isOther ? 1 : 2) : step}
        total={suspended ? 1 : step === OTHER_STEP ? (solution?.isOther ? 2 : 3) : TOTAL_STEPS}
        title="Orçamento Imediato"
        desc={suspended ? "" : stepDesc}
        footer={suspended ? null : footer}
      >
        {suspended ? (
          <SuspendedNotice
            title={suspended.title}
            message={suspended.message}
            onClose={close}
            testId="orc-suspended"
          />
        ) : (
          <>
        {/* Banner de retoma de progresso */}
        {step === 0 && resumeSnapshot && !loadingCatalog && solutions.length > 0 && (
          <div
            data-testid="orc-resume-banner"
            className="mb-4 rounded-lg border border-[#1698AC]/30 bg-[#1698AC]/5 px-3 py-2.5 flex items-center gap-3"
          >
            <RotateCcw size={16} className="text-[#1698AC] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                Tem um orçamento por terminar
              </p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 dark:text-slate-300">
                Retome onde ficou ou comece de novo.
              </p>
            </div>
            <button
              type="button"
              onClick={resumeProgress}
              data-testid="orc-resume-btn"
              className="text-xs font-semibold text-white bg-[#1698AC] hover:bg-[#0F7484] rounded-full px-3 py-1.5 transition-colors shrink-0"
            >
              Retomar
            </button>
            <button
              type="button"
              onClick={discardProgress}
              data-testid="orc-discard-btn"
              className="text-xs font-medium text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-white transition-colors shrink-0"
            >
              Recomeçar
            </button>
          </div>
        )}

        {/* STEP 0 · Solução */}
        {step === 0 && (
          <StepList
            loading={loadingCatalog}
            error={catalogError}
            items={[...solutions, OTHER_SOLUTION]}
            emptyMsg="Sem soluções disponíveis."
            testId="orc-solutions-grid"
            renderItem={(s) => (
              <OptionCard
                key={s.id}
                testId={`orc-solucao-${s.slug}`}
                selected={solution?.id === s.id}
                onClick={() => {
                  setSolution(s);
                  setServico(null);
                  setConfig(null);
                  // Ao trocar de solução, respostas anteriores podem já não
                  // corresponder às novas perguntas — limpamos para evitar
                  // um `answersValid` fantasma.
                  setAnswers({});
                }}
                label={s.title}
                desc={s.short_desc}
                image={!s.isOther ? resolveImage(s.image) : ""}
                icon={Layers}
              />
            )}
          />
        )}

        {/* STEP 1 · Serviço */}
        {step === 1 && (
          <div className="space-y-4">
            <ContextChip
              items={[
                { icon: Layers, label: "Solução", value: solution?.title },
              ]}
            />
            <StepList
              loading={false}
              items={[...servicesOfSolution, OTHER_SERVICE]}
              emptyMsg="Esta solução ainda não tem serviços associados."
              testId="orc-services-grid"
              renderItem={(s) => (
                <OptionCard
                  key={s.id}
                  testId={`orc-servico-${s.slug}`}
                  selected={servico?.id === s.id}
                  onClick={() => {
                    setServico(s);
                    // Novo serviço → novo config → respostas anteriores obsoletas
                    setAnswers({});
                    setConfig(null);
                  }}
                  label={s.title}
                  desc={s.short_desc}
                  image={!s.isOther ? resolveImage(s.image) : ""}
                  icon={Wrench}
                />
              )}
            />
          </div>
        )}

        {/* STEP 4 · Distrito (LOCALIZAÇÃO — agora no final antes da identidade) */}
        {step === 4 && (
          <div className="space-y-4">
            <ContextChip items={[
              { icon: Layers, label: "Solução", value: solution?.title },
              { icon: Wrench, label: "Serviço", value: servico?.title },
              chosenTypologyObj?.label ? { icon: Layers, label: config?.typology_label || "Tipologia", value: chosenTypologyObj.label } : null,
            ].filter(Boolean)} />
            <div className="space-y-1.5">
              <Label htmlFor="orc-district" className="flex items-center gap-1.5">
                <MapPin size={12} className="text-[#1698AC]" /> Distrito
                <span className="text-red-500">*</span>
              </Label>
              <select
                id="orc-district"
                data-testid="orc-district-select"
                value={districtId}
                onChange={(e) => setDistrictId(e.target.value)}
                className="w-full h-10 bg-white dark:bg-[#0A0E16] border border-slate-300 dark:border-white/15 rounded-lg px-3 text-sm text-slate-900 dark:text-slate-100 focus:border-[#1698AC] focus:outline-none focus:ring-2 focus:ring-[#1698AC]/25"
              >
                <option value="">— Escolha o distrito —</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Código postal — obrigatório, permite estimativa precisa por zona */}
            <div className="space-y-1.5">
              <Label htmlFor="orc-postal" className="flex items-center gap-1.5">
                <Mailbox size={12} className="text-[#1698AC]" /> Código postal
                <span className="text-red-500" aria-hidden>*</span>
              </Label>
              <input
                id="orc-postal"
                data-testid="orc-postal-input"
                type="text"
                inputMode="numeric"
                placeholder="1000-100"
                maxLength={8}
                value={postalCode}
                required
                onChange={(e) => {
                  // formatar automaticamente para NNNN-NNN
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 7);
                  const formatted = digits.length > 4 ? `${digits.slice(0, 4)}-${digits.slice(4)}` : digits;
                  setPostalCode(formatted);
                }}
                className="w-full h-10 bg-white dark:bg-[#0A0E16] border border-slate-300 dark:border-white/15 rounded-lg px-3 text-sm font-mono text-slate-900 dark:text-slate-100 focus:border-[#1698AC] focus:outline-none focus:ring-2 focus:ring-[#1698AC]/25"
              />
              <p className="text-[11px] text-slate-500 dark:text-slate-300 leading-snug">
                Necessário para calcular o preço final com condições específicas da sua zona.
              </p>
              {/* Aviso Distrito × CP — visível quando ambos estão preenchidos
                  mas o prefixo do CP não corresponde ao distrito selecionado */}
              {cpDistrictCheck.status === "mismatch" && (
                <div
                  data-testid="orc-cp-district-warning"
                  className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 flex items-start gap-2"
                >
                  <AlertCircle size={13} className="mt-0.5 shrink-0 text-amber-600" />
                  <span>
                    O código postal <span className="font-semibold font-mono">{postalCode}</span>{" "}
                    parece não pertencer a{" "}
                    <span className="font-semibold">
                      {districts.find((d) => d.id === districtId)?.name}
                    </span>
                    {cpDistrictCheck.suggestions && cpDistrictCheck.suggestions.length > 0 && (
                      <>
                        {" "}— habitualmente é de{" "}
                        <span className="font-semibold">
                          {cpDistrictCheck.suggestions
                            .map((c) => districtNameByCode[c] || c)
                            .filter(Boolean)
                            .join(" ou ")}
                        </span>
                      </>
                    )}
                    . Verifique o distrito ou o CP antes de continuar para evitar orçamentos enviesados.
                  </span>
                </div>
              )}
              {/* Widget de cobertura por CP — status em tempo real */}
              {coverageLoading && (
                <div
                  data-testid="orc-coverage-loading"
                  className="text-[11px] text-slate-500 dark:text-slate-300 flex items-center gap-1.5 mt-1"
                >
                  <Loader2 size={12} className="animate-spin" /> A verificar cobertura da zona…
                </div>
              )}
              {!coverageLoading && coverage && (
                coverage.covered ? (
                  <div
                    data-testid="orc-coverage-ok"
                    className={`text-xs rounded-lg px-3 py-2 border flex items-start gap-2 mt-1 ${
                      coverage.distance_applies
                        ? "bg-sky-50 border-sky-200 text-sky-800"
                        : "bg-emerald-50 border-emerald-200 text-emerald-800"
                    }`}
                  >
                    <MapPin size={13} className={`mt-0.5 shrink-0 ${coverage.distance_applies ? "text-sky-600" : "text-emerald-600"}`} />
                    <span>{coverage.message}</span>
                  </div>
                ) : (
                  <div
                    data-testid="orc-coverage-out"
                    className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded-lg px-3 py-2 flex items-start gap-2 mt-1"
                  >
                    <AlertCircle size={13} className="mt-0.5 shrink-0 text-rose-600" />
                    <span>{coverage.message}</span>
                  </div>
                )
              )}
            </div>
            {loadingConfig && (
              <p className="text-xs text-slate-400 dark:text-slate-300 flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> A verificar disponibilidade…
              </p>
            )}
            {configError && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{configError}</span>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 · Tipologia (apenas) — obrigatória quando existir */}
        {step === 2 && config && (
          <div className="space-y-4" data-testid="orc-questions">
            {(solution || servico) && (
              <ContextChip items={[
                { icon: Layers, label: "Solução", value: solution?.title },
                { icon: Wrench, label: "Serviço", value: servico?.title },
              ]} />
            )}
            {otherMode && config.intro_message && (
              <div className="text-xs bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 flex items-start gap-2">
                <Info size={12} className="text-[#1698AC] dark:text-[#4CD3E5] mt-0.5 shrink-0" />
                <div className="text-slate-600 dark:text-slate-200">{config.intro_message}</div>
              </div>
            )}
            {hasTypologies && (
              <div className="space-y-2" data-testid="orc-typology">
                <div>
                  <Label className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 dark:text-white">
                    <Layers size={13} className="text-[#1698AC] dark:text-[#4CD3E5]" />
                    {config.typology_label || "Tipologia"}
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-600 bg-rose-50 border border-rose-100 dark:text-white dark:bg-rose-500/25 dark:border-rose-500/40 rounded px-1.5 py-0.5 ml-1">
                      Obrigatória
                    </span>
                  </Label>
                  {config.typology_help && (
                    <p className="text-[11px] text-slate-500 dark:text-slate-300 mt-0.5">{config.typology_help}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1" data-testid="orc-typology-list">
                  {(config.typologies || []).map((t) => {
                    const isSelected = answers.typology === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          // Semeia defaults das perguntas complementares desta tipologia (sem apagar respostas já dadas)
                          const tInit = {};
                          (t.questions || []).forEach((q) => {
                            if (q.type === "number") tInit[q.key] = q.default_value ?? q.min_value ?? 1;
                            else if (q.type === "yesno") tInit[q.key] = false;
                            else if (q.type === "single") tInit[q.key] = q.options?.[0]?.value || "";
                            else if (q.type === "multi") tInit[q.key] = [];
                          });
                          setAnswers({ ...tInit, ...answers, typology: t.id });
                        }}
                        className={
                          "w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm font-medium " +
                          (isSelected
                            ? "bg-[#1698AC]/10 text-[#0F7484] dark:bg-[#1698AC]/25 dark:text-white font-semibold"
                            : "text-slate-700 dark:text-white hover:bg-slate-50 dark:bg-white/5 dark:hover:bg-white/5")
                        }
                        data-testid={`orc-typology-${t.id}`}
                      >
                        <span
                          className={
                            "w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center " +
                            (isSelected ? "border-[#1698AC] dark:border-[#4CD3E5]" : "border-slate-300 dark:border-white/40")
                          }
                        >
                          {isSelected && <span className="w-2 h-2 rounded-full bg-[#1698AC] dark:bg-[#4CD3E5]" />}
                        </span>
                        <span className="flex-1 min-w-0">{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 3 · Restantes perguntas dinâmicas (sem tipologia) */}
        {step === 3 && config && (
          <div className="space-y-4" data-testid="orc-questions-others">
            <ContextChip items={[
              { icon: Layers, label: "Solução", value: solution?.title },
              { icon: Wrench, label: "Serviço", value: servico?.title },
              chosenTypologyObj?.label ? { icon: Layers, label: config?.typology_label || "Tipologia", value: chosenTypologyObj.label } : null,
            ].filter(Boolean)} />
            {/* Perguntas complementares da tipologia escolhida — MOSTRADAS PRIMEIRO */}
            {((config.typologies || []).find((t) => t.id === answers.typology)?.questions || []).map((q) => (
              <QuestionInput
                key={q.key}
                q={q}
                value={answers[q.key]}
                onChange={(v) => setAnswers({ ...answers, [q.key]: v })}
              />
            ))}
            {/* Perguntas globais do serviço — DEPOIS das complementares da tipologia */}
            {(config.questions || []).map((q) => (
              <QuestionInput
                key={q.key}
                q={q}
                value={answers[q.key]}
                onChange={(v) => setAnswers({ ...answers, [q.key]: v })}
              />
            ))}
          </div>
        )}

        {/* STEP 5 · Identidade + Morada — recolhidos ANTES da estimativa */}
        {step === 5 && (
          <form
            id="orc-form"
            onSubmit={(e) => {
              e.preventDefault();
              // No step 5 o botão do footer trata da validação e faz avançar
              // para o step 6 (confirmação). Este submit só existe para tecla ↵
              const { isValid, errors, firstErrorField } = validateIdentity(id, {
                phoneRequired: true,
                postalRequired: false,
              });
              if (!isValid) {
                setIdTouched({ nome: true, email: true, telefone: true });
                toast.error(buildErrorSummary(errors));
                focusFirstError(firstErrorField, "orc");
                return;
              }
              setStep(6);
            }}
            className="space-y-4"
          >
            <Honeypot inputRef={hpRef} valueRef={hpValRef} />
            <ContextChip items={[
              { icon: Layers, label: "Solução", value: solution?.title },
              { icon: Wrench, label: "Serviço", value: servico?.title },
              chosenTypologyObj?.label ? { icon: Layers, label: config?.typology_label || "Tipologia", value: chosenTypologyObj.label } : null,
              districtId ? { icon: MapPin, label: "Distrito", value: districts.find((d) => d.id === districtId)?.name } : null,
              postalCode ? { icon: Mailbox, label: "Código postal", value: postalCode } : null,
            ].filter(Boolean)} />
            <IdentityFields
              prefix="orc"
              values={id}
              onChange={setId}
              errors={identityErrors}
              touched={idTouched}
              onBlur={setIdBlur}
            />
            {/* Morada opcional — útil para agilizar um posterior agendamento */}
            <div className="space-y-1.5">
              <Label htmlFor="orc-morada" className="flex items-center gap-1.5">
                <MapPin size={12} className="text-[#1698AC]" /> Morada do local
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  Opcional
                </span>
              </Label>
              <Textarea
                id="orc-morada"
                data-testid="orc-morada-input"
                rows={2}
                placeholder="Ex.: Rua da República, 123, 3.º Esq. — Marinha Grande"
                value={morada}
                onChange={(e) => {
                  setMorada(e.target.value);
                  setMoradaUserEdited(true);
                }}
                className="resize-none"
              />
              <p className="text-[11px] text-slate-500 dark:text-slate-300 leading-snug">
                {postalLoading
                  ? "A procurar morada do código postal…"
                  : "Se preencher, pode agendar o serviço logo a seguir sem repetir os dados."}
              </p>
            </div>
          </form>
        )}

        {/* STEP 5 · Confirmação dos dados — a estimativa é revelada só no modal final */}
        {step === 5 && (
          <div className="space-y-4">
            <div className="text-xs bg-[#1698AC]/8 dark:bg-[#1698AC]/15 border border-[#1698AC]/20 dark:border-[#1698AC]/40 text-[#0F7484] dark:text-white rounded-lg px-3 py-2 flex items-center gap-2 font-medium">
              <Info size={12} /> Confira os dados antes de confirmar. O orçamento é revelado a seguir.
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 divide-y divide-slate-100 dark:divide-white/10 overflow-hidden">
              {(() => {
                // Cada linha do resumo é um botão que devolve ao respectivo
                // passo em modo edição. O restante progresso é preservado.
                const jumpTo = (target) => {
                  setEditingFromSummary(true);
                  setStep(target);
                };
                const rowClass =
                  "group w-full text-left flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[#1698AC]/5 dark:hover:bg-[#1698AC]/15 focus:outline-none focus:bg-[#1698AC]/8 dark:focus:bg-[#1698AC]/20";
                const editBadgeClass =
                  "text-[10px] text-[#1698AC] dark:text-[#4CD3E5] font-semibold uppercase tracking-wider inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity";
                return (
                  <>
                    {/* Solução → step 0 */}
                    <button
                      type="button"
                      data-testid="orc-summary-solution"
                      onClick={() => jumpTo(0)}
                      className={rowClass}
                      aria-label="Editar solução"
                    >
                      <Layers size={14} className="text-[#1698AC] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-300 font-semibold">Solução</p>
                        <p className="text-sm text-slate-800 dark:text-white font-medium truncate">{solution?.title}</p>
                      </div>
                      <span className={editBadgeClass}>
                        <Pencil size={10} /> Editar
                      </span>
                    </button>

                    {/* Serviço → step 1 */}
                    <button
                      type="button"
                      data-testid="orc-summary-servico"
                      onClick={() => jumpTo(1)}
                      className={rowClass}
                      aria-label="Editar serviço"
                    >
                      <Wrench size={14} className="text-[#1698AC] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-300 font-semibold">Serviço</p>
                        <p className="text-sm text-slate-800 dark:text-white font-medium truncate">{servico?.title}</p>
                      </div>
                      <span className={editBadgeClass}>
                        <Pencil size={10} /> Editar
                      </span>
                    </button>

                    {/* Tipologia (opcional, só se o par tem tipologias definidas) → step 2 */}
                    {(() => {
                      const chosen = (config?.typologies || []).find((t) => t.id === answers.typology);
                      if (!chosen) return null;
                      return (
                        <button
                          type="button"
                          data-testid="orc-summary-typology"
                          onClick={() => jumpTo(2)}
                          className={rowClass}
                          aria-label="Editar tipologia"
                        >
                          <Layers size={14} className="text-[#1698AC] shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-300 font-semibold">
                              {config?.typology_label || "Tipologia"}
                            </p>
                            <p className="text-sm text-slate-800 dark:text-white font-medium truncate">{chosen.label}</p>
                          </div>
                          <span className={editBadgeClass}>
                            <Pencil size={10} /> Editar
                          </span>
                        </button>
                      );
                    })()}

                    {/* Localização → step 4 (distrito + CP). Nova ordem: agora
                        depois de tipologia/perguntas e antes da identidade. */}
                    <button
                      type="button"
                      data-testid="orc-summary-location"
                      onClick={() => jumpTo(4)}
                      className={rowClass}
                      aria-label="Editar distrito ou código postal"
                    >
                      <MapPin size={14} className="text-[#1698AC] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-300 font-semibold">Localização</p>
                        <p className="text-sm text-slate-800 dark:text-white font-medium truncate">
                          {districts.find((d) => d.id === districtId)?.name}
                          {postalCode ? ` · ${postalCode}` : ""}
                        </p>
                        {morada?.trim() && (
                          <p className="text-[11px] text-slate-500 dark:text-slate-300 mt-0.5 truncate">{morada.trim()}</p>
                        )}
                      </div>
                      <span className={editBadgeClass}>
                        <Pencil size={10} /> Editar
                      </span>
                    </button>

                    {/* Contacto (nome/email/telefone + morada) → step 5 */}
                    <button
                      type="button"
                      data-testid="orc-summary-contact"
                      onClick={() => jumpTo(5)}
                      className={rowClass}
                      aria-label="Editar contacto e morada"
                    >
                      <User size={14} className="text-[#1698AC] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-300 font-semibold">Contacto</p>
                        <p className="text-sm text-slate-800 dark:text-white font-medium truncate">{id?.nome}</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-300 truncate">
                          {id?.email}
                          {id?.telefone ? ` · ${id.telefone}` : ""}
                        </p>
                      </div>
                      <span className={editBadgeClass}>
                        <Pencil size={10} /> Editar
                      </span>
                    </button>
                  </>
                );
              })()}
            </div>

            <p className="text-[10px] text-slate-400 dark:text-slate-300 leading-snug text-center">
              Clique em qualquer linha para corrigir os dados sem perder o restante.
            </p>

            {estimateLoading && (
              <p className="text-[11px] text-slate-500 dark:text-slate-300 flex items-center gap-1.5">
                <Loader2 size={10} className="animate-spin" /> A preparar o seu orçamento…
              </p>
            )}
            {estimateError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                {estimateError}
              </div>
            )}
            {!estimateLoading && !estimateError && estimate && null}
          </div>
        )}

        {/* STEP OUTRO · Descrição livre + Identidade */}
        {step === OTHER_STEP && (
          <form
            id="orc-form-other"
            onSubmit={(e) => {
              e.preventDefault();
              finishOther();
            }}
            className="space-y-4"
          >
            <Honeypot inputRef={hpRef} valueRef={hpValRef} />
            {(solution?.title || servico?.title) && !solution?.isOther && !servico?.isOther && (
              <ContextChip items={[
                { icon: Layers, label: "Solução", value: solution?.title },
                { icon: Wrench, label: "Serviço", value: servico?.title },
              ]} />
            )}
            <div className="text-xs bg-[#1698AC]/8 dark:bg-[#1698AC]/15 border border-[#1698AC]/20 dark:border-[#1698AC]/40 text-[#0F7484] dark:text-white rounded-lg px-3 py-2 flex items-start gap-2">
              <Info size={13} className="mt-0.5 shrink-0" />
              <span>
                Não é possível calcular um valor automático. Descreva o que pretende e a nossa equipa responde com uma proposta.
              </span>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="orc-other-desc">
                Descreva o que pretende <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="orc-other-desc"
                data-testid="orc-other-desc"
                rows={4}
                placeholder="Ex.: Preciso de uma inspeção/certificação para… (indique o tipo de instalação, localização e qualquer detalhe útil)"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
              />
            </div>
            <IdentityFields
              prefix="orc"
              values={id}
              onChange={setId}
              errors={identityErrors}
              touched={idTouched}
              onBlur={setIdBlur}
            />
          </form>
        )}
        </>
        )}

        {/* Modal aninhado — confirmação de saída (substitui window.confirm) */}
        {showExitConfirm && (
          <div
            className="absolute inset-0 z-30 flex items-center justify-center px-4"
            data-testid="orc-exit-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="orc-exit-confirm-title"
          >
            <div
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              onClick={cancelExit}
            />
            <div className="relative w-full max-w-sm bg-white dark:bg-[#0A0E16] rounded-2xl shadow-2xl border border-slate-200 dark:border-white/10 p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                  <AlertTriangle size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    id="orc-exit-confirm-title"
                    className="text-base font-semibold text-slate-900 dark:text-slate-100"
                  >
                    Sair sem guardar?
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 dark:text-slate-300 mt-1">
                    Os dados preenchidos serão perdidos. Tem a certeza que deseja fechar o orçamento?
                  </p>
                </div>
              </div>
              <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <button
                  type="button"
                  onClick={cancelExit}
                  data-testid="orc-exit-cancel"
                  className="h-10 px-4 rounded-full text-sm font-semibold text-slate-700 dark:text-slate-200 bg-white dark:bg-transparent border border-slate-300 dark:border-white/15 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                >
                  Continuar a preencher
                </button>
                <button
                  type="button"
                  onClick={confirmExit}
                  data-testid="orc-exit-confirm-btn"
                  className="h-10 px-4 rounded-full text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors"
                >
                  Sair sem guardar
                </button>
              </div>
            </div>
          </div>
        )}
      </WizardShell>

      <SuccessModal
        open={!!successData}
        onClose={() => {
          setSuccessData(null);
        }}
        title={successData?.isZeroEstimate ? "Pedido registado!" : "Pedido de orçamento registado!"}
        eyebrow="ORÇAMENTO"
        name={successData?.name}
        email={successData?.email}
        highlight={successData?.highlight}
        details={successData?.details || []}
        assurance={successData?.assurance}
        secondaryTagline={successData?.secondaryTagline}
        closeLabel={successData?.closeLabel || "Concluído"}
        secondaryAction={
          successData?.prefill && !successData?.isZeroEstimate
            ? {
                label: "Agendar Online agora",
                icon: CalendarDays,
                onClick: () => {
                  const detail = successData.prefill;
                  setSuccessData(null);
                  // Pequeno atraso para o modal fechar suavemente antes de abrir o próximo
                  setTimeout(() => {
                    window.dispatchEvent(
                      new CustomEvent("open-agendamento", { detail }),
                    );
                  }, 120);
                },
              }
            : null
        }
        onCloseAction={() => {
          // "Corrigir Dados" — fecha o modal de sucesso e reabre o wizard com
          // prefill para o utilizador rever/corrigir o pedido.
          if (!successData?.prefill) {
            setSuccessData(null);
            return;
          }
          const detail = successData.prefill;
          setSuccessData(null);
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("open-orcamento", { detail }),
            );
          }, 120);
        }}
        testId="orc-success-modal"
      />
    </>
  );
}

// ============================================================================
// Helpers
// ============================================================================
function ContextChip({ items }) {
  const rows = (items || []).filter((it) => it && it.value);
  if (rows.length === 0) return null;
  return (
    <div
      className="text-xs bg-[#1698AC]/8 dark:bg-[#1698AC]/15 border border-[#1698AC]/20 dark:border-[#1698AC]/40 text-[#0F7484] dark:text-white rounded-lg px-3 py-2 space-y-0.5 font-medium"
      data-testid="orc-context-chip"
    >
      {rows.map((it, i) => (
        <div key={i} className="flex items-center gap-1.5">
          {it.icon ? <it.icon size={12} className="shrink-0 dark:text-[#4CD3E5]" /> : <span className="w-3" />}
          <span className="shrink-0 dark:text-slate-300">{it.label}:</span>
          <strong className="font-semibold truncate dark:text-white">{it.value}</strong>
        </div>
      ))}
    </div>
  );
}

function StepList({ loading, error, items, emptyMsg, testId, renderItem }) {
  if (loading)
    return (
      <div className="flex items-center justify-center py-10 text-slate-400 dark:text-slate-300">
        <Loader2 size={18} className="animate-spin mr-2" /> A carregar…
      </div>
    );
  if (error)
    return (
      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
        {error}
      </div>
    );
  if (!items || items.length === 0)
    return (
      <div className="text-sm text-slate-500 dark:text-slate-300 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg p-3">
        {emptyMsg}
      </div>
    );
  return (
    <div
      data-lenis-prevent
      className="grid gap-2 max-h-80 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-visible pr-1"
      data-testid={testId}
    >
      {items.map((it) => renderItem(it))}
    </div>
  );
}

function QuestionInput({ q, value, onChange }) {
  const req = q.required ? <span className="text-red-500">*</span> : null;

  if (q.type === "yesno") {
    return (
      <div className="border border-slate-200 dark:border-white/10 rounded-lg p-3">
        <label className="flex items-center gap-3 cursor-pointer">
          <button
            type="button"
            onClick={() => onChange(!value)}
            className={`shrink-0 w-11 h-6 rounded-full transition-colors relative ${
              value ? "bg-[#1698AC]" : "bg-slate-300"
            }`}
            role="switch"
            aria-checked={!!value}
            data-testid={`orc-q-${q.key}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                value ? "translate-x-5" : ""
              }`}
            />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 dark:text-white">
              {q.label} {req}
            </p>
            {q.help && <p className="text-[11px] text-slate-500 dark:text-slate-300 mt-0.5">{q.help}</p>}
          </div>
        </label>
      </div>
    );
  }

  if (q.type === "number") {
    return (
      <div>
        <Label>
          {q.label} {req}
        </Label>
        {q.help && <p className="text-[11px] text-slate-500 dark:text-slate-300 mb-1">{q.help}</p>}
        <Input
          type="number"
          min={q.min_value ?? undefined}
          max={q.max_value ?? undefined}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          data-testid={`orc-q-${q.key}`}
        />
      </div>
    );
  }

  if (q.type === "single") {
    return (
      <div>
        <Label>
          {q.label} {req}
        </Label>
        {q.help && <p className="text-[11px] text-slate-500 dark:text-slate-300 mb-1">{q.help}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
          {(q.options || []).map((o) => (
            <OptionCard
              key={o.value}
              testId={`orc-q-${q.key}-${o.value}`}
              selected={value === o.value}
              onClick={() => onChange(o.value)}
              label={o.label}
            />
          ))}
        </div>
      </div>
    );
  }

  // multi
  return (
    <div>
      <Label>
        {q.label} {req}
      </Label>
      {q.help && <p className="text-[11px] text-slate-500 dark:text-slate-300 mb-1">{q.help}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
        {(q.options || []).map((o) => {
          const arr = value || [];
          const isSel = arr.includes(o.value);
          return (
            <OptionCard
              key={o.value}
              testId={`orc-q-${q.key}-${o.value}`}
              selected={isSel}
              onClick={() =>
                onChange(isSel ? arr.filter((x) => x !== o.value) : [...arr, o.value])
              }
              label={o.label}
            />
          );
        })}
      </div>
    </div>
  );
}
